import i18next from '@/i18n';
import { EToastType, EToastVariant, IToastOpts, toastManager } from '@/stores/feedback';
import { SafeTimeout } from '@/utils/safeTimeout';

/** 各 variant 的默认停留时长（ms） */
const DEFAULT_TIMEOUT: Record<EToastVariant, number> = {
  [EToastVariant.Alert]: 3000,
  [EToastVariant.Toast]: 2000
};

/**
 * 传给 base-ui 的停留时长恒为 0，即禁用它自带的计时器。
 *
 * base-ui 在悬停 toast、viewport 获得键盘焦点、窗口失焦时都会暂停倒计时，
 * 与本项目「只在窗口失焦时暂停」的需求不符，因此计时全部由本模块接管。
 */
const MANAGER_TIMEOUT = 0;

/**
 * 仍然可以被 update 的 toast id。
 *
 * base-ui 对已移除、或正处于关闭动画中的 toast 会忽略 update，
 * 因此复用 id 前必须先判断它是否还活着，否则消息会被静默丢弃。
 */
const liveIds = new Set<string>();

/** 被 limit 挤出可见区（`data-limited`）的 toast id，这期间暂停倒计时 */
const limitedIds = new Set<string>();

const timers = new Map<string, SafeTimeout>();

let windowFocused = document.hasFocus();

/** 暂停 / 继续都是幂等的，可以对全部计时器无条件重放 */
function applyRunState(timer: SafeTimeout, toastId: string) {
  if (windowFocused && !limitedIds.has(toastId)) {
    timer.resume();
  } else {
    timer.pause();
  }
}

function clearTimer(toastId: string) {
  const timer = timers.get(toastId);
  if (!timer) {
    return;
  }

  timer.clear();
  timers.delete(toastId);
}

/** 每次 add / update 都重新计时，同一条 toast 被复用时停留时长从头算起 */
function scheduleTimer(toastId: string, timeout: number) {
  clearTimer(toastId);

  if (!timeout) {
    return;
  }

  const timer = SafeTimeout.create();
  timer.start(timeout, () => {
    timers.delete(toastId);
    toastManager.close(toastId);
  });

  timers.set(toastId, timer);
  applyRunState(timer, toastId);
}

/** 任何关闭路径（自动关闭、上滑、Toast.Close、手动 remove）都会走到这里 */
function handleClose(toastId: string) {
  liveIds.delete(toastId);
  limitedIds.delete(toastId);
  clearTimer(toastId);
}

function setWindowFocused(focused: boolean) {
  if (windowFocused === focused) {
    return;
  }

  windowFocused = focused;
  timers.forEach(applyRunState);
}

/**
 * 弹出一条 toast，返回它的 id。
 *
 * 传入的 `id` 对应的 toast 仍然存在时原地更新，否则新建一条。
 */
export function addToast(options: IToastOpts): string {
  const variant = options.variant ?? EToastVariant.Toast;
  const type = options.type ?? EToastType.REGULAR;
  // loading 没有确定的结束时机，只能由调用方复用同一 id 更新或显式关闭
  const timeout = type === EToastType.LOADING ? 0 : (options.timeout ?? DEFAULT_TIMEOUT[variant]);
  const { title, content } = options;

  const payload = {
    title: title || content,
    description: title ? content : null,
    type,
    timeout: MANAGER_TIMEOUT,
    actionProps: options.onCancel
      ? {
          children: options.onCancelText ?? i18next.t('common.cancel'),
          onClick: options.onCancel
        }
      : undefined,
    data: { variant }
  };

  if (options.id && liveIds.has(options.id)) {
    toastManager.update(options.id, payload);
    scheduleTimer(options.id, timeout);
    return options.id;
  }

  // id 由 base-ui 生成，add 返回后才能拿到，onClose 经闭包读取
  const handle = { id: '' };
  handle.id = toastManager.add({ ...payload, onClose: () => handleClose(handle.id) });

  liveIds.add(handle.id);
  scheduleTimer(handle.id, timeout);
  return handle.id;
}

/** 关闭指定 toast */
export function removeToast(toastId: string) {
  toastManager.close(toastId);
  // toast 已经不在列表中时 close 不触发 onClose，这里兜底清理
  handleClose(toastId);
}

/**
 * 同步 base-ui 的 limited 标记。被 limit 挤出可见区的 toast 不应该继续倒计时，
 * 否则它可能在重新显现之前就已经关闭，用户完全看不到。
 *
 * @param toasts base-ui 当前的 toast 列表，只取 id 与 limited 两个字段
 */
export function syncToastLimited(toasts: readonly { id: string; limited?: boolean | undefined }[]) {
  limitedIds.clear();
  for (const toast of toasts) {
    if (toast.limited) {
      limitedIds.add(toast.id);
    }
  }

  timers.forEach(applyRunState);
}

/** 监听窗口焦点：失焦期间暂停全部倒计时，重新聚焦后继续。由 ToastRenderer 挂载时调用一次 */
export function bindToastFocusListener() {
  const handleFocus = () => setWindowFocused(true);
  const handleBlur = () => setWindowFocused(false);

  window.addEventListener('focus', handleFocus);
  window.addEventListener('blur', handleBlur);

  return () => {
    window.removeEventListener('focus', handleFocus);
    window.removeEventListener('blur', handleBlur);
  };
}
